import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Tabs,
  Table,
  Space,
  Badge,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  TagOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const { TabPane } = Tabs;

const ThemeDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("1");

  // API hooks
  const { execute: getTheme, loading: themeLoading } = useDynamicApi("GET");

  // Load theme details
  const loadThemeDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.THEMES_UPDATE}/${id}`;
      const response = await getTheme(endpoint);

      if (response?.status === true && response.data) {
        setTheme(response.data);
      } else {
        Toast.error(t("themeManager.failedFetchDetails"));
        navigate("/theme");
      }
    } catch (error) {
      console.error("Error fetching theme details:", error);
      Toast.error(error.message || t("themeManager.failedFetchDetails"));
      navigate("/theme");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadThemeDetails();
    }
  }, [id]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading || themeLoading) return null;

  if (!theme) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h3>{t("themeManager.noThemesFound")}</h3>
          <Button type="primary" onClick={() => navigate("/theme")}>
            {t("common.previous")} {t("themeManager.title")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="theme-details-page">
      {/* Header with back button and actions - Exactly like UserDetails */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/theme")}
          >
            {t("common.previous")} {t("themeManager.title")}
          </Button>
          <h2 style={{ margin: 0 }}>{t("themeManager.details")}</h2>
        </Space>
        {/* Edit button commented out like in UserDetails */}
        {/* <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/themes/edit/${theme.id}`)}
        >
          Edit Theme
        </Button> */}
      </div>

      {/* Main Content - Tabs exactly like UserDetails */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Details Tab - Like Personal Information Tab in UserDetails */}
        <TabPane tab={t("themeManager.details")} key="1">
          <Card>
            {/* Avatar and Title Section - Exactly like UserDetails */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <div>
                <h1 style={{ margin: 0, marginBottom: "8px" }}>{theme.name}</h1>
                <Space size="middle">
                  <Tag color={theme.is_active ? "green" : "default"}>
                    {theme.is_active
                      ? t("common.active")
                      : t("common.inactive")}
                  </Tag>
                  <Badge
                    status={theme.is_active ? "success" : "default"}
                    text={
                      theme.is_active
                        ? t("common.active")
                        : t("common.inactive")
                    }
                  />
                </Space>
              </div>
            </div>

            {/* Theme Details in Descriptions - Like UserDetails */}
            <Descriptions bordered column={2}>
              <Descriptions.Item label={t("common.name")} span={1}>
                <Space>
                  <TagOutlined />
                  {theme.name}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label={t("common.description")} span={1}>
                {theme.id}
              </Descriptions.Item>

              <Descriptions.Item label={t("common.status")} span={1}>
                <Tag color={theme.is_active ? "green" : "default"}>
                  {theme.is_active ? t("common.active") : t("common.inactive")}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label={t("common.createdAt")} span={1}>
                <Space>
                  <CalendarOutlined />
                  {formatDate(theme.created_at)}
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label={t("common.lastUpdated")} span={1}>
                <Space>
                  <ClockCircleOutlined />
                  {formatDate(theme.updated_at)}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ThemeDetails;
